import React, { useEffect, useState } from 'react';
import { Box, Button, Grid, Typography } from "@mui/material";
import Bottom from "../Bottom";
import Top from "../Top";
import HistoryLoadState from "../../components/HistoryLoadState";
import RadialChart from "../../components/RadialChart";
import LineChart from "../../components/LineChart";
import Stack from '@mui/material/Stack';
import Avatar from '@mui/material/Avatar';
import Divider from '@mui/material/Divider';
import historyweight from "../../assets/icon/historyweight.gif";
import ArrowLeftIcon from "@mui/icons-material/ArrowLeft";
import ArrowRightIcon from "@mui/icons-material/ArrowRight";
import { useNavigate } from "react-router-dom";
import * as util from "../../utils/util";
import api from "../../utils/api";
import HistorySliderInput from "../../components/HistorySliderInput";
import { notifyApiError } from "../../utils/apiError";

const params = {
  token: '',
  from: '',
  to: '',
  target: ''
}

const initTargetWeight = {
  weight: 0
}

const HistoryWeight = () => {
  const navigate = useNavigate();
  const [dayValue, setDayValue] = useState(0);
  /**
   * 화면 상단 날짜. 서버 응답이 아니라 dayValue 에서 바로 만든다.
   *
   * 예전에는 빈 문자열로 시작해 조회 성공(result_code === '0') 안에서만 채웠다.
   * 그래서 (1) 응답이 올 때까지 '..' 만 보였고 (2) 조회가 실패하면 영영 '..' 로 남았다.
   * 날짜는 클라이언트가 이미 아는 값이라 서버 응답에 묶어 둘 이유가 없다.
   */
  const targetDate = util.getDateByCustom('yyyyMMdd', dayValue);
  const [loading, setLoading] = React.useState(true);
  const [failed, setFailed] = React.useState(false);
  const [reloadNonce, setReloadNonce] = React.useState(0);
  const [targetWeight, setTargetWeight] = React.useState(initTargetWeight);
  const [series, setSeries] = React.useState([{ curve: 'linear', data: [0] }]);
  const [xAxis, setXAxis] = React.useState([{ scaleType: 'point', data: [0] }]);

  useEffect(() => {
    setWeightData();
  }, [dayValue, reloadNonce]);

  function setWeightData() {
    setLoading(true);
    setFailed(false);
    // 체중 정보 조회
    params.token = localStorage.getItem('token') || '';
    params.from = util.getDateByCustom('yyyyMMdd', dayValue - 7);
    params.to = util.getDateByCustom('yyyyMMdd', dayValue);
    params.target = util.getDateByCustom('yyyyMMdd', dayValue);

    api.get(`app/api/v1/AP_002_018?token=${params.token}&from=${params.from}&to=${params.to}&target=${params.target}`).then(res => {
      if (res.data.result_code === '0') {
        setTargetWeight(res.data.result_data.target);

        let tempArr = [];
        for (let item of res.data.result_data.data.weight_v2) {
          let obj = { weight: 0, month: '' };
          obj.month = item.date;
          obj.weight = item.weight;
          tempArr.push(obj);
        }

        setSeries([
          {
            curve: 'linear',
            data: tempArr.map((v: any) => v.weight),
          },
        ]);
        setXAxis([
          { scaleType: 'point',
            data: tempArr.map((v: any) => v.month),
          },
        ]);
      } else {
        // result_code 가 '0' 이 아니면 조회 실패다. 예전에는 아무 처리도 없어서
        // 수치는 0 인 채로, 날짜는 '..' 로 굳고 사용자에게는 아무 안내도 없었다.
        console.warn('[기록] 조회 실패 result_code=', res.data.result_code, res.data.result_message);
        setFailed(true);
      }
    }).catch(function (error) {
      console.log(error);
      setFailed(true);
    }).finally(function () {
      setLoading(false);
    });
  }

  // 체중 저장
  function saveWeight(data: any) {
    const saveParams = { date: '', weight: 0 };
    saveParams.date = targetDate.slice(0, 4) + '-' + targetDate.slice(4, 6) + '-' + targetDate.slice(6, 8);
    saveParams.weight = data;

    api.post(`app/api/v1/AP_002_019`, saveParams).then(res => {
      setWeightData();
    }).catch(function (error) {
      // 저장 실패를 알리지 않으면 사용자는 기록이 남은 줄 안다
      notifyApiError(error, '체중 기록을 저장하지 못했습니다.');
    });
  }



  return (
    <Stack sx={{ width: '100%', minWidth: 320, background: '#F1F2F4', pb: 12, pl: 2, pr: 2, boxSizing: 'border-box' }} spacing={6}>
      <Top title="체중 기록" />

      <Grid sx={{ bgcolor: '#F1F2F4', p: 0, width: '100%', minHeight: 500 }}>
        <Box>
          <Typography component="div" sx={{ fontSize: 20, color: '#555', m: 4, mb: 0, textAlign: 'center', fontWeight: 700 }}>
            <ArrowLeftIcon onClick={() => setDayValue((prev: any) => prev - 1)} sx={{ verticalAlign: 'text-bottom' }} />
            {targetDate.slice(0, 4) + '.' + targetDate.slice(4, 6) + '.' + targetDate.slice(6, 8)}
            <ArrowRightIcon onClick={() => setDayValue((prev: any) => prev + 1)} sx={{ 'display': dayValue < 0 ? 'inline-block' : 'none', verticalAlign: 'text-bottom' }} />
          </Typography>

          {/* 조회 중 / 실패 표시 — 날짜 바로 아래 한 줄 */}
          <HistoryLoadState loading={loading} failed={failed} onRetry={() => setReloadNonce((n) => n + 1)} />

          <Stack direction={'row'} justifyContent={'center'} sx={{ m: '20px 0', p: 1, bgcolor: '#fff', borderRadius: '15px', boxShadow: '0px 2px 1px -1px rgba(0, 0, 0, 0.2), 0px 1px 1px 0px rgba(0, 0, 0, 0.14), 0px 1px 3px 0px rgba(0, 0, 0, 0.12)' }}>
            <Grid>
              <RadialChart value={targetWeight.weight} />
            </Grid>

            <Stack justifyContent={'center'} sx={{ m: 'auto', }}>
              <Avatar alt="" src={historyweight} sx={{ m: 'auto', width: '60px', height: '60px', borderRadius: 0 }} />
            </Stack>

            <Stack direction={'row'} alignItems={'center'} sx={{ ml: 'auto', mr: 2 }}>
              <Typography component="div" sx={{ fontSize: 30, color: '#279700', fontWeight: 700 }}>{targetWeight.weight}</Typography>
              <Typography component="div" sx={{ fontSize: 18, color: '#777', textAlign: 'center', ml: 1, }}> kg</Typography>
            </Stack>
          </Stack>

          {/* <Accordion defaultExpanded sx={{ borderRadius: '15px', position: 'static' }}>
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              aria-controls="panel1a-content"
              id="panel1a-header"
              sx={{
                '&#panel1a-header': { minHeight: '55px' },
                '& .MuiAccordionSummary-content.Mui-expanded': { m: 0 }
              }}
            >
              <Typography sx={{ fontSize: 16, color: '#555', fontWeight: 700, }}>체중 기록</Typography>
            </AccordionSummary>
            <Divider />

            <AccordionDetails>


              <Stack direction={'row'} alignItems={'center'} justifyContent={'space-between'} pb={1}>
                <Typography component="div" sx={{ fontSize: 14, color: '#555', fontWeight: 500 }}>10시 30분</Typography>
                <Typography component="div" sx={{ fontSize: 18, color: '#333', fontWeight: 700, textAlign: 'right', mr: 1 }}>80
                  <Typography component="span" sx={{ fontSize: 14, color: '#999', }}> kg</Typography>
                </Typography>
              </Stack>

            </AccordionDetails>
          </Accordion> */}

          <Stack justifyContent={'center'} alignItems={'center'} sx={{ width:'100%'}}>
            <HistorySliderInput info={{ value: targetWeight.weight, title: '오늘의 체중을 입력하세요', unit: 'kg', btnTitle: '입력 하기', max: 150 }} handleComplete={saveWeight} />
          </Stack>

          <Box sx={{ bgcolor: '#fff', p: '0 10px 10px 10px', borderRadius: '15px', boxShadow: '0px 2px 1px -1px rgba(0, 0, 0, 0.2), 0px 1px 1px 0px rgba(0, 0, 0, 0.14), 0px 1px 3px 0px rgba(0, 0, 0, 0.12)' }}>
            <Stack direction={'row'} justifyContent={'center'} alignItems={'center'} sx={{ p: 1 }}>
              <Stack >
                <Typography component="div" sx={{ fontSize: 14, color: '#555', }}>최근 7일</Typography>
              </Stack>
              <Stack sx={{ ml: 'auto', zIndex: 2 }} >
                <Button sx={{ fontSize: 12, color: '#555', }} endIcon={<ArrowRightIcon />}
                  onClick={() => { navigate('../history/WeightMonthHistory', { state: { type: 'weight' } }) }}>
                  월간현황
                </Button>
              </Stack>
            </Stack>
            <Divider />

            <Box sx={{
              mt: -3.5,
            }}>
              <LineChart series={series} xAxis={xAxis} isReferenceLine={true} />

            </Box>
          </Box>
        </Box>
      </Grid>

      <Bottom />
    </Stack>
  );
}

export default HistoryWeight;